import { Shield, Droplets } from "lucide-react";

export default function FailSafe() {
  return (
    <section
      id="failsafe"
      data-testid="failsafe-section"
      className="relative py-32 lg:py-44 bg-white overflow-hidden"
    >
      {/* background */}
      <div className="absolute inset-0 tech-grid opacity-25" />
      <div className="absolute inset-0 radial-fade" />

      <div className="max-w-7xl mx-auto px-6 lg:px-12 relative">
        {/* heading */}
        <div className="text-center max-w-4xl mx-auto mb-20 reveal">
          <div className="inline-flex items-center gap-3 mb-6">
            <span className="spec-label">MWP1110 · Fail-Safe Coolant Pump Design</span>
          </div>
          <h2 className="font-chivo font-black text-5xl sm:text-6xl lg:text-7xl tracking-tighter text-[#1A2232] leading-[0.92]">
            When a water pump fails,<br />
            it usually fails <span className="text-[#FF5722]">closed</span>.
          </h2>
          <p className="mt-5 font-plex text-lg lg:text-xl text-[#536073] max-w-2xl mx-auto leading-relaxed">
            Coolant stops. The engine overheats.<br />
            The driver finds out the hard way.
          </p>
          <p className="mt-7 font-plex text-base lg:text-lg text-[#536073] max-w-2xl mx-auto leading-relaxed">
            The MPS MWP1110 inverts the entire failure model. A reinforced spring, a debris-shielded secondary pump and a redesigned impeller path conspire to do one thing — if ANY part of the switching mechanism fails, the shield retracts to the OPEN position. The pump returns to full unrestricted flow. The engine cools normally. The driver never knows there was a problem.
          </p>
        </div>

        {/* Diagram comparison */}
        <div className="grid lg:grid-cols-2 gap-6 lg:gap-10 reveal">
          {/* Others — closed */}
          <div className="relative bg-white border border-[#C8D3DF] rounded-sm p-8 lg:p-10 overflow-hidden">
            <div className="absolute inset-0 diagonal-stripes opacity-15 pointer-events-none" />
            <div className="flex items-center gap-3 mb-6 relative">
              <span className="w-2 h-2 rounded-full bg-[#FF5722]" />
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#FF5722] uppercase">
                OEM & Aftermarket
              </span>
            </div>
            <div className="font-chivo font-black text-2xl lg:text-3xl text-[#1A2232] mb-6">
              Fails <span className="text-[#FF5722]">CLOSED</span>.
            </div>

            {/* Closed valve diagram */}
            <ValveDiagram closed />

            <div className="mt-6 space-y-2 font-plex text-sm text-[#536073]">
              <p>→ Coolant flow stops at the shield.</p>
              <p>→ Cylinder head temperature spikes.</p>
              <p>→ Head gasket failure. Warped block.</p>
              <p>→ Warranty claim within 2,000 km.</p>
            </div>
          </div>

          {/* MPS — open */}
          <div className="relative bg-gradient-to-br from-white to-[#EEF3FA] border border-[#5B9BD5]/40 rounded-sm p-8 lg:p-10 overflow-hidden glow-cyan">
            <div className="flex items-center gap-3 mb-6 relative">
              <span className="w-2 h-2 bg-[#5B9BD5]" />
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#5B9BD5] uppercase">
                MPS MWP1110
              </span>
            </div>
            <div className="font-chivo font-black text-2xl lg:text-3xl text-[#1A2232] mb-6">
              Locks <span className="text-[#5B9BD5]">OPEN</span>.
            </div>

            {/* Open valve diagram */}
            <ValveDiagram />

            <div className="mt-6 space-y-2 font-plex text-sm text-[#536073]">
              <p>→ Shield retracts to full-open position.</p>
              <p>→ 100% coolant flow restored.</p>
              <p>→ Engine cools as if pump were brand new.</p>
              <p>→ Customer drives home. Zero claim.</p>
            </div>
          </div>
        </div>

        {/* Bottom feature */}
        <div className="mt-16 grid lg:grid-cols-2 gap-10 items-center reveal">
          <div className="relative">
            <img
              src="/assets/ewp1110_render2.png"
              alt="MWP1110 cross-section showing open coolant flow path"
              className="w-full object-contain"
            />
          </div>
          <div>
            <div className="flex items-center gap-3 mb-4">
              <Shield size={16} className="text-[#5B9BD5]" />
              <span className="font-mono text-[10px] tracking-[0.3em] text-[#5B9BD5] uppercase">
                Engineering Logic
              </span>
            </div>
            <h3 className="font-chivo font-black text-3xl lg:text-4xl tracking-tighter text-[#1A2232] mb-5">
              The reinforced return spring that keeps VAG engines safe.
            </h3>
            <p className="font-plex text-[#536073] leading-relaxed">
              The original VAG return spring is undersized — it cannot recover the main piston once contamination intrudes. MPS engineers thickened the spring wire and tuned the rate to a higher pre-load. Combined with the integrated inlet filter that stops abrasive debris from ever reaching the secondary pump, the MWP1110 is built so the spring <span className="text-[#1A2232] font-semibold">always wins</span>.
            </p>
            <div className="mt-6 flex flex-wrap gap-3">
              {["Reinforced wire gauge", "Higher pre-load", "Filter-protected", "Open-state bias"].map((t) => (
                <span
                  key={t}
                  className="px-3 py-1.5 border border-[#C8D3DF] font-mono text-[10px] tracking-[0.2em] text-[#536073] uppercase"
                >
                  {t}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

function ValveDiagram({ closed = false }) {
  return (
    <div className="relative h-32 bg-white border border-[#E8EEF6] rounded-sm overflow-hidden">
      {/* pipe walls */}
      <div className="absolute inset-x-0 top-4 h-px bg-[#C8D3DF]" />
      <div className="absolute inset-x-0 bottom-4 h-px bg-[#C8D3DF]" />

      {/* flow arrows */}
      {!closed && (
        <>
          {[...Array(8)].map((_, i) => (
            <div
              key={i}
              className="absolute top-1/2 -translate-y-1/2 text-[#5B9BD5] font-mono"
              style={{
                left: `${5 + i * 12}%`,
                animation: `marqueeFlow 1.8s linear ${i * 0.15}s infinite`,
              }}
            >
              ▸
            </div>
          ))}
        </>
      )}
      {closed && (
        <div className="absolute inset-0 flex items-center justify-center font-mono text-[10px] tracking-[0.3em] text-[#FF5722]/70 uppercase">
          ◼ FLOW BLOCKED ◼
        </div>
      )}

      {/* shield */}
      <div
        className={`absolute top-1/2 -translate-y-1/2 ${closed ? "left-1/2 -translate-x-1/2" : "left-[8%]"
          } transition-all duration-700`}
      >
        <div
          className={`${closed ? "w-1.5 h-24 bg-[#FF5722]" : "w-1.5 h-8 bg-[#5B9BD5]"
            }`}
        />
      </div>

      <style>{`
        @keyframes marqueeFlow {
          from { opacity: 0; transform: translateY(-50%) translateX(0); }
          50% { opacity: 1; }
          to { opacity: 0; transform: translateY(-50%) translateX(40px); }
        }
      `}</style>
    </div>
  );
}
